/**
 * Cross-target WebSocket client — thin transport, no React state.
 *
 * Distilled from ``ando-frontend/app/components/contexts/SocketContext.tsx``
 * (960 LOC monolite v1.x). The React state (interrupted query, pending
 * query recovery, toast plumbing) lives in the app's Zustand slice — this
 * class owns ONLY the protocol: connect/disconnect/send/onMessage +
 * heartbeat + reconnect.
 *
 * Behaviour mirrored from v1:
 *   - Exponential backoff with jitter: ``min(1000 * 2^n + jitter, 30_000)``.
 *   - Up to 10 reconnection attempts before giving up.
 *   - Application-level heartbeat every 30 s; if no ``pong`` within 30 s
 *     AND no outbound activity (status callback in ``connecting`` for query
 *     in flight), the socket is closed.
 *   - Page Visibility API: re-check liveness when the tab becomes visible.
 *
 * Design notes:
 *   - Status machine: ``idle → connecting → connected → reconnecting →
 *     closed``. The host app subscribes via ``onStatusChange``.
 *   - Listeners are stored by ``type`` (event ``type`` field). ``onMessage``
 *     returns a cleanup that removes the handler — no leaks.
 *   - Unknown / malformed messages are forwarded to the optional
 *     ``onUnknownMessage`` callback for observability.
 */

export type WSStatus = "idle" | "connecting" | "connected" | "reconnecting" | "closed";

export interface WSConnectOptions {
  /** Full WS URL (e.g. ``wss://api.example.com/query_pydantic``). */
  url: string;
  /** Device fingerprint forwarded as a header / query param (BE expects ``device_id``). */
  deviceId?: string;
  /** Status transitions. Called synchronously. */
  onStatusChange?: (status: WSStatus) => void;
  /** Optional: notified when a frame fails to parse JSON. */
  onUnknownMessage?: (raw: string, err: unknown) => void;
  /**
   * Bearer access token provider — mirrors ``ApiClient.AuthTokenProvider``.
   * When set (Tauri shell), the resolved token is appended as ``?token=…``
   * so the BE can authenticate the socket without a cookie. Re-read on
   * every (re)connect so silent-refresh rotations take effect immediately.
   * Leave unset on the web (cookie path).
   */
  getAccessToken?: () => Promise<string | null> | string | null;
}

type Listener = (event: unknown) => void;

const HEARTBEAT_INTERVAL_MS = 30_000;
const HEARTBEAT_TIMEOUT_MS = 30_000;
const BASE_RECONNECT_DELAY_MS = 1_000;
const MAX_RECONNECT_DELAY_MS = 30_000;
const MAX_RECONNECT_ATTEMPTS = 10;

export class WSClient {
  private socket: WebSocket | null = null;
  private opts: WSConnectOptions | null = null;
  private statusInternal: WSStatus = "idle";
  private readonly listeners = new Map<string, Set<Listener>>();
  private reconnectAttempts = 0;
  private explicitClose = false;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private heartbeatTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private visibilityHandler: (() => void) | null = null;
  private lastPongAt = 0;
  private openingPromise: Promise<void> | null = null;

  get status(): WSStatus {
    return this.statusInternal;
  }

  /** Open the socket. Resolves when the first ``open`` event fires. */
  connect(opts: WSConnectOptions): Promise<void> {
    this.opts = opts;
    this.explicitClose = false;
    this.attachVisibilityHandler();
    if (this.socket?.readyState === WebSocket.OPEN) {
      opts.onStatusChange?.("connected");
      return Promise.resolve();
    }
    return this.openSocket();
  }

  /** Idempotent — closes the socket and stops all timers. */
  disconnect(): void {
    this.explicitClose = true;
    this.clearTimers();
    this.detachVisibilityHandler();
    this.openingPromise = null;
    if (this.socket) {
      try {
        this.socket.close();
      } catch {
        // already closed
      }
      this.socket = null;
    }
    this.setStatus("closed");
  }

  /** Send a JSON-serialisable frame. Throws if the socket isn't OPEN. */
  send<T>(event: T): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      throw new Error(`WSClient.send: socket not OPEN (status=${this.statusInternal})`);
    }
    this.socket.send(JSON.stringify(event));
  }

  /**
   * Ensure the socket is ``connected`` within ``timeoutMs``. If the
   * connection is already up, resolves immediately. Otherwise resets
   * the reconnect-attempts counter (so a session that already
   * exhausted its budget — e.g. after a BE restart — gets one more
   * deliberate retry) and races the reconnect against the timeout.
   *
   * Used at user-initiated send sites (composer ``send``) so a
   * transient drop is recovered transparently instead of bouncing the
   * send to a REST fallback that may not exist BE-side. Rejects with
   * a typed ``Error("WSClient.ensureConnected: timeout")`` when the
   * deadline elapses — the caller surfaces this as a chat error chip.
   */
  async ensureConnected(opts: { timeoutMs?: number } = {}): Promise<void> {
    const timeoutMs = opts.timeoutMs ?? 3_000;
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      return;
    }
    if (!this.opts) {
      // No prior connect() call — nothing to retry against.
      throw new Error("WSClient.ensureConnected: connect() never called");
    }
    // Fresh retry budget for this user-initiated attempt. Without
    // this, a session that already exhausted MAX_RECONNECT_ATTEMPTS
    // (e.g. tab left open across a BE restart) is permanently dead.
    this.reconnectAttempts = 0;
    this.explicitClose = false;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    return await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        unsubscribe();
        reject(new Error("WSClient.ensureConnected: timeout"));
      }, timeoutMs);

      // Subscribe to status changes; resolve as soon as we see
      // ``connected``. The opt.onStatusChange handler stays untouched —
      // we layer on top via a one-shot wrapper.
      const original = this.opts?.onStatusChange;
      const unsubscribe = (): void => {
        clearTimeout(timer);
        // Restore original handler so other listeners are unaffected.
        if (this.opts) this.opts.onStatusChange = original;
      };
      if (this.opts) {
        this.opts.onStatusChange = (next) => {
          original?.(next);
          if (next === "connected") {
            unsubscribe();
            resolve();
          }
        };
      }

      // Kick the connect — openSocket triggers status transitions
      // observed above. Errors during open also trigger the wrapped
      // status callback via the existing scheduleReconnect path.
      void this.openSocket().catch(() => {
        // openSocket already drives the status machine — no extra
        // handling needed; the timeout above is the deadline.
      });
    });
  }

  /** Subscribe to messages with a given ``type``. Returns an unsubscribe fn. */
  onMessage<T = unknown>(type: string, handler: (event: T) => void): () => void {
    let set = this.listeners.get(type);
    if (!set) {
      set = new Set();
      this.listeners.set(type, set);
    }
    const wrapped: Listener = (e) => handler(e as T);
    set.add(wrapped);
    return () => {
      const current = this.listeners.get(type);
      if (current) {
        current.delete(wrapped);
        if (current.size === 0) this.listeners.delete(type);
      }
    };
  }

  // ---------------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------------

  private setStatus(next: WSStatus): void {
    if (this.statusInternal === next) return;
    this.statusInternal = next;
    this.opts?.onStatusChange?.(next);
  }

  private async openSocket(): Promise<void> {
    if (this.socket?.readyState === WebSocket.OPEN) return;
    if (this.openingPromise) return this.openingPromise;

    const opening = this.createSocket();
    this.openingPromise = opening;
    try {
      await opening;
    } finally {
      if (this.openingPromise === opening) this.openingPromise = null;
    }
  }

  private async createSocket(): Promise<void> {
    if (!this.opts) throw new Error("WSClient: connect() not called");
    const { url, deviceId, getAccessToken } = this.opts;

    this.setStatus(this.reconnectAttempts > 0 ? "reconnecting" : "connecting");

    // Re-read the token on every (re)connect — silent refresh may have
    // rotated it since the previous open.
    let token: string | null = null;
    if (getAccessToken) {
      try {
        token = await getAccessToken();
      } catch {
        // Token resolution failure → fall through and let the BE 401 cleanly.
      }
    }

    let finalUrl = url;
    if (deviceId) finalUrl = appendQuery(finalUrl, "device_id", deviceId);
    if (token) finalUrl = appendQuery(finalUrl, "token", token);

    return new Promise<void>((resolve, reject) => {
      let settled = false;
      let socket: WebSocket;
      try {
        socket = new WebSocket(finalUrl);
      } catch (err) {
        reject(err);
        this.scheduleReconnect();
        return;
      }
      this.socket = socket;

      socket.onopen = () => {
        if (this.socket !== socket || this.explicitClose) {
          try {
            socket.close();
          } catch {
            // stale socket already closed
          }
          if (!settled) {
            settled = true;
            reject(new Error("WebSocket opening superseded"));
          }
          return;
        }
        this.reconnectAttempts = 0;
        this.lastPongAt = Date.now();
        this.setStatus("connected");
        this.startHeartbeat();
        if (!settled) {
          settled = true;
          resolve();
        }
      };

      socket.onmessage = (event) => {
        if (this.socket !== socket) return;
        const raw = typeof event.data === "string" ? event.data : "";
        if (raw.length === 0) return;
        let parsed: unknown;
        try {
          parsed = JSON.parse(raw);
        } catch (err) {
          this.opts?.onUnknownMessage?.(raw, err);
          return;
        }
        if (!parsed || typeof parsed !== "object") {
          this.opts?.onUnknownMessage?.(raw, new Error("Non-object payload"));
          return;
        }
        const typeField = (parsed as { type?: unknown }).type;
        if (typeField === "pong") {
          this.lastPongAt = Date.now();
          if (this.heartbeatTimeoutTimer) {
            clearTimeout(this.heartbeatTimeoutTimer);
            this.heartbeatTimeoutTimer = null;
          }
        }
        if (typeof typeField === "string") {
          const set = this.listeners.get(typeField);
          if (set) {
            for (const handler of set) handler(parsed);
          }
        }
      };

      socket.onerror = () => {
        // ``onclose`` will run right after with the right reason — keep this
        // a no-op to avoid double status transitions.
      };

      socket.onclose = () => {
        const isCurrent = this.socket === socket;
        if (isCurrent) {
          this.stopHeartbeat();
          this.socket = null;
        }
        if (this.explicitClose) {
          if (isCurrent) this.setStatus("closed");
          if (!settled) {
            settled = true;
            reject(new Error("WebSocket closed before opening"));
          }
          return;
        }
        if (!settled) {
          settled = true;
          reject(new Error("WebSocket closed before opening"));
        }
        if (isCurrent) this.scheduleReconnect();
      };
    });
  }

  private scheduleReconnect(): void {
    if (this.explicitClose) return;
    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      this.setStatus("closed");
      return;
    }
    this.setStatus("reconnecting");
    const exp = BASE_RECONNECT_DELAY_MS * 2 ** this.reconnectAttempts;
    const jitter = Math.random() * 1_000;
    const delay = Math.min(exp + jitter, MAX_RECONNECT_DELAY_MS);
    this.reconnectAttempts += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      // Fire and forget — the promise is consumed by us, not the caller.
      void this.openSocket().catch(() => {
        // openSocket already calls scheduleReconnect on failure
      });
    }, delay);
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => this.sendHeartbeat(), HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.heartbeatTimeoutTimer) {
      clearTimeout(this.heartbeatTimeoutTimer);
      this.heartbeatTimeoutTimer = null;
    }
  }

  private sendHeartbeat(): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;
    try {
      this.socket.send(JSON.stringify({ type: "ping" }));
    } catch {
      // socket dying — let onclose handle it
      return;
    }
    this.heartbeatTimeoutTimer = setTimeout(() => {
      const elapsed = Date.now() - this.lastPongAt;
      if (elapsed > HEARTBEAT_INTERVAL_MS + HEARTBEAT_TIMEOUT_MS) {
        // No pong seen for a full cycle — force a reconnect.
        try {
          this.socket?.close();
        } catch {
          // ignore
        }
      }
    }, HEARTBEAT_TIMEOUT_MS);
  }

  private clearTimers(): void {
    this.stopHeartbeat();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private attachVisibilityHandler(): void {
    if (typeof document === "undefined") return;
    if (this.visibilityHandler) return;
    this.visibilityHandler = () => {
      if (document.visibilityState !== "visible") return;
      if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
        // We came back to a dead socket — kick a reconnect cycle.
        if (!this.reconnectTimer && !this.explicitClose) this.scheduleReconnect();
        return;
      }
      // Liveness check.
      try {
        this.socket.send(JSON.stringify({ type: "ping" }));
      } catch {
        try {
          this.socket.close();
        } catch {
          // ignore
        }
      }
    };
    document.addEventListener("visibilitychange", this.visibilityHandler);
  }

  private detachVisibilityHandler(): void {
    if (typeof document === "undefined") return;
    if (!this.visibilityHandler) return;
    document.removeEventListener("visibilitychange", this.visibilityHandler);
    this.visibilityHandler = null;
  }
}

function appendQuery(url: string, key: string, value: string): string {
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
}
